// Auto-generated lightweight class for PDMPart
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { PDMPartReadAPI, PDMPartWriteAPI, PDMPartReadAPISchema, PDMPartWriteAPISchema } from '../types/pdmpart';
import { ModelRegistry } from '../../registries/model-registry';
import { Allocation, AtScope, HasMembers, HasPorts, HasRootPart, PartComposedOf, Performs, Specializes } from '../../relationships/relationship';

import { PDMPort } from './pdmport';
import { PartDefinition } from '../../sysml/api/partdefinition';
import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { SysMLType } from '../../sysml/api/sysmltype';
import { ActionDefinition } from '../../sysml/api/actiondefinition';
import { PDMProduct } from './pdmproduct';



export interface PDMPart extends Partial<PDMPartReadAPI> {}
export class PDMPart extends NeoNodeBase<PDMPartReadAPI, PDMPartWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "PDMPart";
  static readonly readSchema = PDMPartReadAPISchema;
  static readonly writeSchema = PDMPartWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    general: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "out" },
    specific: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "in" },
    allocated_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "Allocation", direction: "in" },
    performed_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "Performs", direction: "out" },
    sub_parts: { target: "PartDefinition", cardinality: "many", isParent: false, relClass: "PartComposedOf", direction: "out" },
    parent_part: { target: "PartDefinition", cardinality: "one", isParent: true, relClass: "PartComposedOf", direction: "in" },
    product: { target: "PDMProduct", cardinality: "one", isParent: true, relClass: "HasRootPart", direction: "in" },
    ports: { target: "PDMPort", cardinality: "many", isParent: false, relClass: "HasPorts", direction: "out" },
  };

  protected get modelName(): string {
    return 'PDMPart';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return PDMPart.label;
  }


  constructor(data: Partial<PDMPartWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: PDMPartReadAPI, loadContext?: Record<string, any>): PDMPart {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { allocated_actions, performed_actions, sub_parts, parent_part, product, ports, security_scope, member_namespace, general, specific, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = PDMPartReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, allocated_actions: allocated_actions, performed_actions: performed_actions, sub_parts: sub_parts, parent_part: parent_part, product: product, ports: ports, security_scope: security_scope, member_namespace: member_namespace, general: general, specific: specific };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return PDMPart.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'allocated_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'performed_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'sub_parts', 'PartDefinition', ModelRegistry);
    loadManyRelationship(processed, 'parent_part', 'PartDefinition', ModelRegistry);
    loadOneRelationship(processed, 'product', 'PDMProduct', ModelRegistry);
    loadManyRelationship(processed, 'ports', 'PDMPort', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'general', 'SysMLType', ModelRegistry);
    loadManyRelationship(processed, 'specific', 'SysMLType', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new PDMPart(processed as any);

    return instance;
  }


  /**
   * Delete this instance from the server.
   *
   * @param options - Delete options
   * @param options.cascade - Whether to cascade delete related nodes (default: true for most models)
   * @param options.dryRun - If true, return what would be deleted without actually deleting
   * @returns DeleteResult with deleted IDs and count
   *
   * @example
   * ```typescript
   * const pdmpart = await PDMPart.get(id).first();
   * await pdmpart.delete();  // Delete with default cascade
   *
   * // Or with options
   * const result = await pdmpart.delete({ cascade: false, dryRun: true });
   * console.log('Would delete:', result.deletedIds);
   * ```
   */
  async delete(options: {
    cascade?: boolean;
    dryRun?: boolean;
  } = {}): Promise<{ deletedIds: string[]; count: number }> {
    return this._deleteInstance(options);
  }

  // Relationship manager for 'ports' - Python ORM style (cached)
  get ports(): RelationshipManager<this, PDMPort> {
    return this.getOrCreateRelationshipManager('ports', () => {
      return new RelationshipManager(this, 'ports', 'many', HasPorts as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_parts' - Python ORM style (cached)
  get sub_parts(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('sub_parts', () => {
      return new RelationshipManager(this, 'sub_parts', 'many', PartComposedOf as typeof Relationship);
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'general' - Python ORM style (cached)
  get general(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('general', () => {
      return new RelationshipManager(this, 'general', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'specific' - Python ORM style (cached)
  get specific(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('specific', () => {
      return new RelationshipManager(this, 'specific', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'allocated_actions' - Python ORM style (cached)
  get allocated_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('allocated_actions', () => {
      return new RelationshipManager(this, 'allocated_actions', 'many', Allocation as typeof Relationship);
    });
  }

  // Relationship manager for 'performed_actions' - Python ORM style (cached)
  get performed_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('performed_actions', () => {
      return new RelationshipManager(this, 'performed_actions', 'many', Performs as typeof Relationship);
    });
  }

  // Relationship manager for 'parent_part' - Python ORM style (cached)
  get parent_part(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('parent_part', () => {
      return new RelationshipManager(this, 'parent_part', 'one', PartComposedOf as typeof Relationship);
    });
  }

  // Relationship manager for 'product' - Python ORM style (cached)
  get product(): RelationshipManager<this, PDMProduct> {
    return this.getOrCreateRelationshipManager('product', () => {
      return new RelationshipManager(this, 'product', 'one', HasRootPart as typeof Relationship);
    });
  }

}
